<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSubmit"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Change '{{ props.user.email }}'
  </DialogHeader>
  <DialogContent>
    <UiInput
      v-model="newEmail"
      label="New email"
      :disabled="loading"
      class="mb-5"
    />
    <UiInput v-model="confirmEmail" label="Confirm email" :disabled="loading" />
    <Message v-if="error" class="mt-5" type="error">
      {{ error }}
    </Message>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      primary
      :loading="loading"
      :disabled="disableButton"
      @click="handleSubmit"
    >
      Update email
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, UiInput, Message, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { computed, ref, watch } from 'vue'
import {
  useUpdateAccountEmailMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'

const { close } = useDialog()
const toastStore = useToastStore()
const webviewStore = useWebviewStore()

const props = defineProps<{
  user: User
}>()

const { mutate: updateEmail, loading } = useUpdateAccountEmailMutation()

const newEmail = ref('')
const confirmEmail = ref('')
const error = ref('')

const disableButton = computed(
  () => !newEmail.value.trim() || !confirmEmail.value.trim()
)

watch([newEmail, confirmEmail], () => {
  error.value = ''
})

const handleSubmit = async () => {
  try {
    if (newEmail.value !== confirmEmail.value) {
      error.value = 'Email does not match.'
      return
    }

    await updateEmail({
      newEmail: newEmail.value,
    })

    if (webviewStore.isActive) {
      webviewStore.postMessage({
        type: NativeActionsEnum.UserUpdated,
      })
    }

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Your email has been updated',
    })

    close()
  } catch (err) {
    console.error(err)
    error.value = (err as Error).message
  }
}
</script>
